import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import GoogleReviews from '../components/GoogleReviews';
import RDVBookingBubble from '../components/RDVBookingBubble';
import ProcessSlider from '../components/ProcessSlider';
import { SERVICE_LIST } from '../data/serviceConfigs';
import {
  CalendarCheck, ArrowRight, ArrowLeft, MapPin, Phone, Clock, MessageCircle,
  ShieldCheck, CheckCircle, Award, Star, Phone as PhoneIcon, AlertTriangle,
} from 'lucide-react';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/jbzie3cv_en-tete.png';

const PROCESS_STEPS_6 = [
  { id: 'rdv', num: 1, title: 'Prise de RDV en ligne', subtitle: 'Choisissez votre véhicule, la date et l\u2019heure depuis votre canapé.', dotLabel: 'Rendez-vous', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/709qcrgw_rendez-vous.png', alt: 'Prise de rendez-vous en ligne — Carrosserie Meyrin' },
  { id: 'depot', num: 2, title: 'Dépose de votre véhicule', subtitle: 'Accueil personnalisé à l\u2019atelier de Meyrin.', dotLabel: 'Dépose', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/khmy3rvm_Prise-en-charge-vehicule.png', alt: 'Prise en charge véhicule CarMaintenance Meyrin' },
  { id: 'diagnostic', num: 3, title: 'Diagnostic & contrôle', subtitle: 'Inspection complète sur pont — Expert 1217 agréé toutes assurances.', dotLabel: 'Diagnostic', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/77xnlugq_contro%CC%82le-du-vehicule.png', alt: 'Diagnostic CarMaintenance Meyrin' },
  { id: 'devis', num: 4, title: 'Devis transparent par SMS', subtitle: 'Validez en un clic, sans surprise.', dotLabel: 'Devis SMS', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/wd5b7w7o_devis-SMS.png', alt: 'Devis SMS CarMaintenance' },
  { id: 'travaux', num: 5, title: 'Travaux après validation', subtitle: 'Aucune intervention sans votre accord.', dotLabel: 'Travaux', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/s4hp4kdb_contro%CC%82le-du-vehicule.png', alt: 'Travaux CarMaintenance après validation' },
  { id: 'restitution', num: 6, title: 'Restitution', subtitle: 'Votre véhicule prêt à reprendre la route.', dotLabel: 'Restitution', image: 'https://customer-assets.emergentagent.com/job_auto-refresh-10/artifacts/yp2etc30_Vehicule-pret.png', alt: 'Remise des clés CarMaintenance Meyrin' },
];

const ServicePage = ({ config }) => {
  const [rdvOpen, setRdvOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', vehicule: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const openRdv = () => {
    setRdvOpen(true);
    window.gtag?.('event', 'lea_open', { page_source: config.slug });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API}/api/contact/garage-meyrin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || '—',
          service: `Page: ${config.slug}${form.vehicule ? ` | Véhicule: ${form.vehicule}` : ''}`,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        window.gtag?.('event', 'form_submit', { page_source: config.slug });
      } else {
        setError('Une erreur est survenue. Réessayez ou appelez le 022 980 93 93.');
      }
    } catch {
      setError('Une erreur est survenue. Réessayez ou appelez le 022 980 93 93.');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faq.map(({ q, a }) => ({
      '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a }
    })),
  };

  const lbSchema = {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    '@id': `https://car-main.ch${config.route}#business`,
    name: `CarMaintenance Meyrin — ${config.heroTitle}`,
    image: HERO_IMG,
    logo: 'https://car-main.ch/logo-carmaintenance.png',
    description: config.metaDesc,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rue des Lattes 62',
      addressLocality: 'Meyrin',
      addressRegion: 'GE',
      postalCode: '1217',
      addressCountry: 'CH',
    },
    geo: { '@type': 'GeoCoordinates', latitude: 46.239567, longitude: 6.0773604 },
    hasMap: 'https://maps.google.com/?cid=3100184985273365445',
    telephone: '+41229809393',
    email: 'info@car-main.ch',
    url: `https://car-main.ch${config.route}`,
    priceRange: 'CHF',
    areaServed: [
      { '@type': 'City', name: 'Meyrin' },
      { '@type': 'City', name: 'Genève' },
      { '@type': 'City', name: 'Vernier' },
      { '@type': 'City', name: 'Grand-Saconnex' },
      { '@type': 'City', name: 'Châtelaine' },
    ],
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '12:00' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '13:30', closes: '18:00' },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '13',
      bestRating: '5',
      worstRating: '1',
    },
    sameAs: ['https://maps.google.com/?cid=3100184985273365445'],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: config.heroTitle,
    name: config.h1,
    description: config.metaDesc,
    provider: { '@id': `https://car-main.ch${config.route}#business` },
    areaServed: [
      { '@type': 'City', name: 'Meyrin' },
      { '@type': 'City', name: 'Genève' },
    ],
    offers: (config.pricing || []).slice(0, 5).map((p) => ({
      '@type': 'Offer',
      name: p.title,
      description: p.desc,
      priceCurrency: 'CHF',
      price: (p.price.match(/\d+/) || ['0'])[0],
      availability: 'https://schema.org/InStock',
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://car-main.ch/' },
      { '@type': 'ListItem', position: 2, name: 'Garage Meyrin', item: 'https://car-main.ch/garage-meyrin' },
      { '@type': 'ListItem', position: 3, name: config.heroTitle, item: `https://car-main.ch${config.route}` },
    ],
  };

  const relatedServices = useMemo(
    () => SERVICE_LIST.filter((s) => s.slug !== config.slug).slice(0, 6),
    [config.slug]
  );

  return (
    <>
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDesc} />
        <meta name="keywords" content={config.keywords} />
        <meta name="geo.region" content="CH-GE" />
        <meta name="geo.placename" content="Meyrin, Genève" />
        <meta name="geo.position" content="46.239567;6.0773604" />
        <meta name="ICBM" content="46.239567, 6.0773604" />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDesc} />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:url" content={`https://car-main.ch${config.route}`} />
        <link rel="canonical" href={`https://car-main.ch${config.route}`} />
        <link rel="alternate" hrefLang="fr-CH" href={`https://car-main.ch${config.route}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://car-main.ch${config.route}`} />
        <link rel="preload" as="image" href={HERO_IMG} fetchPriority="high" />
        <script type="application/ld+json">{JSON.stringify(lbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="bg-white text-gray-900" data-page={config.slug}>
        {/* Top breadcrumb / back to garage-meyrin (positioned below fixed site header) */}
        <div className="bg-gray-50 border-b border-gray-100 pt-16 lg:pt-[72px]">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl py-3 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => navigate('/garage-meyrin')} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#52a675] transition-colors font-medium" data-testid={`back-meyrin-${config.slug}`}>
              <ArrowLeft className="w-4 h-4" />
              Retour à Garage Meyrin
            </button>
            <button onClick={() => navigate('/')} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-testid={`back-home-${config.slug}`}>
              Accueil CarMaintenance
            </button>
          </div>
        </div>

        {/* HERO */}
        <section
          className="relative min-h-[60vh] bg-cover bg-center flex items-center"
          style={{ backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.3) 100%), url('${HERO_IMG}')` }}
          role="img"
          aria-label={config.h1}
        >
          <div className="container mx-auto px-6 py-16 sm:py-20 max-w-6xl">
            <div className="max-w-2xl text-white">
              <div className="inline-flex items-center gap-2 bg-[#52a675]/20 border border-[#52a675]/50 text-[#a8e3c0] px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-sm mb-5">
                <ShieldCheck className="w-3.5 h-3.5" /> Expert 1217 · Agréé toutes assurances
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                {config.heroTitle}
                <span className="block text-[#52a675]">{config.heroAccent}</span>
              </h1>
              {config.srOnlyH2 && (
                <h2 className="sr-only">{config.srOnlyH2}</h2>
              )}
              <p className="mt-5 text-base sm:text-lg text-gray-200 leading-relaxed max-w-xl">{config.heroSubtitle}</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={openRdv} className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-7 py-5 text-base shadow-2xl ring-2 ring-white/20 font-semibold" data-testid={`hero-rdv-${config.slug}`}>
                  <CalendarCheck className="mr-2" /> Demander un devis gratuit
                </Button>
                <a href="#form">
                  <Button size="lg" variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white hover:text-gray-900 px-7 py-5 text-base w-full sm:w-auto">
                    Prenez rendez-vous en ligne <ArrowRight className="ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO + bullets + Pourquoi nous choisir */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">{config.introTitle}</h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-3xl">{config.introBody}</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-10 max-w-3xl">
              {config.bullets.map((b) => (
                <div key={b.title} className="flex gap-3 items-start">
                  <CheckCircle className="w-5 h-5 text-[#52a675] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{b.title}</div>
                    <div className="text-sm text-gray-500">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#f0faf4] border border-[#52a675]/20 rounded-2xl p-6 sm:p-8 max-w-3xl">
              <h3 className="text-xl font-bold mb-4">Pourquoi nous choisir ?</h3>
              <ul className="space-y-2">
                {config.whyChoose.map((w) => (
                  <li key={w} className="flex gap-2 items-center text-gray-700"><CheckCircle className="w-4 h-4 text-[#52a675] shrink-0" />{w}</li>
                ))}
              </ul>
              <Button onClick={openRdv} className="mt-6 bg-[#52a675] hover:bg-[#3d8a5c] text-white">Prendre rendez-vous</Button>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="py-16 sm:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">Tarifs transparents</div>
              <h2 className="text-3xl sm:text-4xl font-bold">{config.pricingTitle}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {config.pricing.map((p) => (
                <div key={p.title} className={`relative bg-white rounded-2xl shadow-lg border p-6 transition-all duration-300 hover:-translate-y-1 ${p.popular ? 'border-[#52a675] ring-2 ring-[#52a675]/30' : 'border-gray-200'}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#52a675] text-white text-xs font-bold px-3 py-1 rounded-full">Populaire</span>}
                  <h3 className="text-lg font-bold mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-600 mb-5 leading-relaxed">{p.desc}</p>
                  <div className="flex items-baseline gap-1 mb-5">
                    <span className="text-3xl font-extrabold text-gray-900">{p.price}</span>
                    {/\d/.test(p.price) && <span className="text-sm text-gray-500">CHF</span>}
                  </div>
                  {p.whatsappMessage ? (
                    <a
                      href={`https://wa.me/41229809393?text=${encodeURIComponent(p.whatsappMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-2.5 rounded-md transition-colors"
                      data-testid={`whatsapp-card-${config.slug}-${p.title.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Demander un devis
                    </a>
                  ) : (
                    <Button onClick={openRdv} className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white">Prendre rendez-vous</Button>
                  )}
                </div>
              ))}
            </div>

            {config.quickDevisWhatsapp && (
              <div className="mt-10 bg-gradient-to-br from-[#25D366]/10 via-white to-[#52a675]/10 ring-1 ring-[#25D366]/30 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid={`quick-devis-whatsapp-${config.slug}`}>
                <div className="max-w-xl">
                  <div className="inline-flex items-center gap-2 bg-[#25D366]/15 text-[#128C7E] text-xs font-bold px-2.5 py-1 rounded-full mb-2">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-1">{config.quickDevisWhatsapp.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{config.quickDevisWhatsapp.desc}</p>
                </div>
                <a
                  href={`https://wa.me/41229809393?text=${encodeURIComponent(config.quickDevisWhatsapp.message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-6 py-3.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  data-testid={`quick-devis-whatsapp-cta-${config.slug}`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Envoyer sur WhatsApp
                </a>
              </div>
            )}
          </div>
        </section>

        {/* SPECIAL SECTIONS */}
        {config.specialSection?.type === 'emergency' && (
          <section className="py-16 sm:py-20 bg-gray-900 text-white">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">Accident ? Nous intervenons immédiatement.</h2>
              <p className="text-gray-300 mb-7 max-w-xl mx-auto leading-relaxed">
                Nous comprenons le stress d&apos;un accident. Notre équipe est disponible pour vous accompagner dans toutes les démarches assurance.
              </p>
              <a href="tel:+41229809393">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold shadow-lg shadow-red-600/30 hover:scale-105 transition-all" data-testid="emergency-call-button">
                  <PhoneIcon className="mr-2 w-5 h-5" /> APPEL DIRECT — 022 980 93 93
                </Button>
              </a>
              <p className="text-sm text-gray-400 mt-4">Disponible 7j/7 · Véhicule de remplacement</p>
            </div>
          </section>
        )}

        {config.specialSection?.type === 'tcs' && (
          <section className="py-12 bg-blue-50 border-y border-blue-100">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3"><Star className="w-3.5 h-3.5 fill-white" /> Tests indépendants</div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">Résultats TCS Pneu Test</h2>
              <p className="text-gray-600">Nous vous conseillons à partir des derniers tests TCS. 2024 : <strong>Michelin CrossClimate 2</strong> et <strong>Continental WinterContact TS 870</strong>. 2025 : <strong>Goodyear Vector 4Seasons Gen-3</strong>. 2026 : <strong>Bridgestone Turanza All Season 6</strong>.</p>
            </div>
          </section>
        )}

        {/* SLIDESHOW 6 ÉTAPES */}
        <section className="py-16 sm:py-20 bg-gradient-to-b from-[#f0f9f4] via-white to-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="text-center mb-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-[#52a675]/10 border border-[#52a675]/20 text-[#3d8a5c] px-3 py-1 rounded-full text-xs font-semibold mb-3">Notre processus</div>
              <h2 className="text-3xl sm:text-4xl font-bold">De votre canapé <span className="text-[#52a675]">au volant</span>, en 6 étapes.</h2>
            </div>
            <ProcessSlider steps={PROCESS_STEPS_6} autoplayMs={5000} />
          </div>
        </section>

        {/* CONTENT SECTIONS (SEO long-form) */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 max-w-4xl space-y-10">
            {config.contentSections.map((s) => (
              <div key={s.h2}>
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">{s.h2}</h2>
                <p className="text-base text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-8">Questions fréquentes</h2>
            <div className="space-y-3">
              {config.faq.map((f) => (
                <details key={f.q} className="bg-white rounded-xl p-5 ring-1 ring-gray-200 group">
                  <summary className="font-semibold cursor-pointer flex items-center justify-between"><span>{f.q}</span><span className="text-[#52a675] group-open:rotate-45 transition-transform text-2xl leading-none">+</span></summary>
                  <p className="mt-3 text-gray-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* RELATED SERVICES — Internal linking for SEO */}
        <section className="py-14 bg-white border-t border-gray-100" aria-label="Autres services du Garage Meyrin">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-[#52a675]/10 border border-[#52a675]/20 text-[#3d8a5c] px-3 py-1 rounded-full text-xs font-semibold mb-2">Nos autres services à Meyrin</div>
              <h2 className="text-2xl sm:text-3xl font-bold">Découvrez tous nos services au <span className="text-[#52a675]">Garage Meyrin</span></h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={s.route}
                  className="group bg-gray-50 hover:bg-[#52a675] hover:text-white ring-1 ring-gray-200 hover:ring-[#52a675] transition-all rounded-xl p-4 text-center"
                  data-testid={`related-service-${s.slug}`}
                >
                  <div className="text-sm font-semibold group-hover:text-white">{s.title}</div>
                  <div className="text-[11px] text-gray-500 group-hover:text-white/80 mt-1">Meyrin · 1217</div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link
                to="/garage-meyrin"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#3d8a5c] hover:text-[#52a675] transition-colors"
                data-testid="link-back-hub-meyrin"
              >
                Voir tous nos services au Garage Meyrin <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* GOOGLE REVIEWS */}
        <section className="py-16 bg-white"><div className="container mx-auto px-4 sm:px-6 max-w-6xl"><GoogleReviews placeId="ChIJixuwCgpjjEcRxfM3kdERBis" /></div></section>

        {/* CONTACT FORM */}
        <section id="form" className="py-16 sm:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">{config.formTitle}</h2>
            <p className="text-gray-600 text-center mb-8">Réponse sous 24h ouvrées.</p>
            {submitted ? (
              <div className="bg-[#f0faf4] border border-[#52a675]/30 rounded-2xl p-8 text-center" data-testid={`form-success-${config.slug}`}>
                <CheckCircle className="w-12 h-12 text-[#52a675] mx-auto mb-3" />
                <p className="font-semibold text-lg">Demande envoyée — merci !</p>
                <p className="text-gray-600 mt-2">Nous revenons vers vous très rapidement.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 ring-1 ring-gray-200 space-y-4" data-testid={`form-${config.slug}`}>
                <input type="hidden" name="source" value={config.slug} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required type="text" placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675]" style={{ fontSize: '16px' }} />
                  <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675]" style={{ fontSize: '16px' }} />
                </div>
                <input type="tel" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675]" style={{ fontSize: '16px' }} />
                <input type="text" placeholder="Véhicule (marque, modèle, année)" value={form.vehicule} onChange={(e) => setForm({ ...form, vehicule: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675]" style={{ fontSize: '16px' }} />
                <textarea required rows={4} placeholder="Décrivez votre besoin *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#52a675]/40 focus:border-[#52a675]" style={{ fontSize: '16px' }} />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white py-6 text-base font-semibold">Envoyer ma demande</Button>
              </form>
            )}
          </div>
        </section>

        {/* INFOS PRATIQUES */}
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
            <div className="grid sm:grid-cols-3 gap-6 text-center">
              <div><MapPin className="w-6 h-6 text-[#52a675] mx-auto mb-2" /><p className="text-sm text-gray-600">Rue des Lattes 62<br />1217 Meyrin · Genève</p></div>
              <div><Clock className="w-6 h-6 text-[#52a675] mx-auto mb-2" /><p className="text-sm text-gray-600">Lun–Ven 08:00–12:00<br />13:30–18:00 · Sam sur RDV</p></div>
              <div><Phone className="w-6 h-6 text-[#52a675] mx-auto mb-2" /><p className="text-sm text-gray-600">022 980 93 93<br /><a href="https://wa.me/41229809393" target="_blank" rel="noopener noreferrer" className="text-[#52a675] inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" />WhatsApp</a></p></div>
            </div>
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="py-16 bg-gradient-to-br from-[#52a675] via-[#3d8a5c] to-[#2e6d47]">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Prêt à confier votre véhicule ?</h2>
            <p className="text-white/90 mb-6">Réservez votre créneau en moins de 60 secondes.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={openRdv} className="bg-white text-[#3d8a5c] hover:bg-gray-100 px-7 py-5 font-semibold"><CalendarCheck className="mr-2" />Prendre rendez-vous</Button>
              <a href="tel:+41229809393"><Button size="lg" variant="outline" className="bg-transparent border-white/60 text-white hover:bg-white hover:text-[#3d8a5c] px-7 py-5"><Phone className="mr-2 w-4 h-4" />022 980 93 93</Button></a>
            </div>
          </div>
        </section>
      </div>

      <RDVBookingBubble open={rdvOpen} onOpenChange={setRdvOpen} pageSource={config.slug} />
    </>
  );
};

export default ServicePage;
